import { useEffect, lazy, Suspense } from "react";
import Layout from "./components/common/Layout.jsx";
import ErrorBoundary from "./components/common/ErrorBoundary.jsx";
import "./App.css";
import { profile } from "./data/linkedinData.js";
import { initSmoothScrolling } from "./utils/smoothScroll";
import { useHashNavigation } from "./hooks/useHashNavigation";
import { preloadCriticalResources } from "./utils/bundleOptimization";
import { createSkipLink } from "./utils/accessibility";
import { useAccessibility } from "./hooks/useAccessibility";
import LazySection from "./components/ui/LazySection";
import SkeletonLoader from "./components/ui/SkeletonLoader";

// Lazy load sections for better performance
const Hero = lazy(() => import("./components/sections/Hero"));
const Projects = lazy(() => import("./components/sections/Projects"));
const Media = lazy(() => import("./components/sections/Media"));
const Publications = lazy(() => import("./components/sections/Publications"));

// Hero section with profile data from LinkedIn
const HeroSection = () => (
  <section
    id="home"
    className="min-h-screen bg-gradient-to-br from-blue-600 to-purple-700 flex items-center justify-center py-20"
    aria-label="Hero section with Ed Lima's introduction"
  >
    <div className="max-w-6xl mx-auto text-center text-white px-6 sm:px-8 lg:px-12">
      <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
        {profile.name}
      </h1>
      <p
        className="text-xl sm:text-2xl md:text-3xl mb-8 font-medium"
        role="doc-subtitle"
      >
        {profile.headline}
      </p>
      <p className="text-lg sm:text-xl opacity-90 max-w-4xl mx-auto mb-12 leading-relaxed">
        {profile.summary}
      </p>

      {/* Expertise areas */}
      <div
        className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-12"
        role="list"
        aria-label="Areas of expertise"
      >
        <div
          className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20 hover:bg-white/20 transition-all duration-300 hover:-translate-y-1"
          role="listitem"
        >
          <h3 className="text-lg font-bold text-white mb-3">Leadership</h3>
          <p className="text-white/90 text-sm leading-relaxed">
            Product, Team Building, Strategy, SaaS
          </p>
        </div>
        <div
          className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20 hover:bg-white/20 transition-all duration-300 hover:-translate-y-1"
          role="listitem"
        >
          <h3 className="text-lg font-bold text-white mb-3">
            Cloud Architecture
          </h3>
          <p className="text-white/90 text-sm leading-relaxed">
            AWS, Azure, Serverless
          </p>
        </div>
        <div
          className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20 hover:bg-white/20 transition-all duration-300 hover:-translate-y-1"
          role="listitem"
        >
          <h3 className="text-lg font-bold text-white mb-3">Data Platforms</h3>
          <p className="text-white/90 text-sm leading-relaxed">
            Applications, APIs, Analytics, Databases
          </p>
        </div>
      </div>

      {/* Call-to-action buttons */}
      <nav
        aria-label="Quick navigation to main sections"
        className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center"
      >
        <button
          onClick={() =>
            document
              .getElementById("media")
              ?.scrollIntoView({ behavior: "smooth" })
          }
          className="w-full sm:w-auto px-8 py-4 bg-white text-blue-700 font-semibold rounded-lg transition-all duration-300 hover:bg-blue-50 hover:shadow-lg hover:scale-105"
          aria-label="Navigate to media section"
        >
          Watch Presentations
        </button>
        <button
          onClick={() =>
            document
              .getElementById("contact")
              ?.scrollIntoView({ behavior: "smooth" })
          }
          className="w-full sm:w-auto px-8 py-4 border-2 border-white text-white font-semibold rounded-lg transition-all duration-300 hover:bg-white/10 hover:shadow-lg hover:scale-105"
          aria-label="Navigate to contact section"
        >
          Contact Me
        </button>
      </nav>
    </div>
  </section>
);

const ContactSection = () => (
  <section
    id="contact"
    className="py-20 lg:py-32 bg-gray-900 dark:bg-gray-950"
    aria-label="Contact information and social media links"
  >
    <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
      <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 text-white">
        Let's Connect
      </h2>
      <div
        className="w-24 h-1 bg-blue-500 mx-auto mb-8"
        aria-hidden="true"
      ></div>
      <p className="text-lg sm:text-xl text-gray-300 mb-12 leading-relaxed max-w-4xl mx-auto">
        I'm always interested in discussing new opportunities, innovative
        projects, and collaborations. Feel free to reach out through any of the
        platforms below.
      </p>
      <nav
        className="flex flex-col sm:flex-row gap-6 justify-center items-center"
        aria-label="Social media and professional profiles"
      >
        <a
          href="https://www.linkedin.com/in/ed1ima/"
          target="_blank"
          rel="noopener noreferrer"
          className="w-full sm:w-auto bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-all duration-300 hover:shadow-lg hover:scale-105"
          aria-label="Connect with Ed Lima on LinkedIn (opens in new tab)"
        >
          Connect on LinkedIn
        </a>
        <a
          href="https://github.com/KoldBrewEd"
          target="_blank"
          rel="noopener noreferrer"
          className="w-full sm:w-auto bg-gray-700 text-white px-8 py-4 rounded-lg font-semibold hover:bg-gray-600 transition-all duration-300 hover:shadow-lg hover:scale-105"
          aria-label="View Ed Lima's GitHub profile (opens in new tab)"
        >
          View GitHub
        </a>
        <a
          href="https://x.com/ednergizer"
          target="_blank"
          rel="noopener noreferrer"
          className="w-full sm:w-auto bg-blue-500 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-400 transition-all duration-300 hover:shadow-lg hover:scale-105"
          aria-label="Follow Ed Lima on X/Twitter (opens in new tab)"
        >
          Follow on X
        </a>
      </nav>
    </div>
  </section>
);

function App() {
  const { announceToScreenReader } = useAccessibility();
  const { navigateToHash } = useHashNavigation();

  useEffect(() => {
    // Set page title
    document.title = "{ed:lima}";

    // Initialize smooth scrolling
    initSmoothScrolling();

    // Preload critical resources
    preloadCriticalResources();

    // Add skip links for accessibility
    const skipToMain = createSkipLink("main-content", "Skip to main content");
    const skipToNav = createSkipLink("main-navigation", "Skip to navigation");

    document.body.insertBefore(skipToMain, document.body.firstChild);
    document.body.insertBefore(skipToNav, document.body.firstChild);

    // Announce page load to screen readers
    announceToScreenReader("Portfolio page loaded", "polite");

    // Cleanup skip links on unmount
    return () => {
      if (skipToMain.parentNode) skipToMain.parentNode.removeChild(skipToMain);
      if (skipToNav.parentNode) skipToNav.parentNode.removeChild(skipToNav);
    };
  }, [announceToScreenReader]);

  // Handle hash navigation after components have loaded
  useEffect(() => {
    // Additional hash handling after all components are likely loaded
    const timer = setTimeout(() => {
      handleInitialHash();
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <Layout>
      {/* Main content wrapper with proper landmark */}
      <main id="main-content" role="main" tabIndex="-1">
        <ErrorBoundary
          title="Hero Section Error"
          message="Unable to load the hero section. Please try refreshing the page."
          showRetry={true}
        >
          <LazySection
            fallback={
              <SkeletonLoader
                className="h-screen bg-gradient-to-br from-blue-600 to-purple-700"
                aria-label="Loading hero section"
              />
            }
          >
            <Suspense
              fallback={
                <SkeletonLoader
                  className="h-screen bg-gradient-to-br from-blue-600 to-purple-700"
                  aria-label="Loading hero section"
                />
              }
            >
              <Hero />
            </Suspense>
          </LazySection>
        </ErrorBoundary>

        {/* Lazy loaded sections with error boundaries */}
        <ErrorBoundary
          title="Media Section Error"
          message="Unable to load the media section. Please try refreshing the page."
          showRetry={true}
        >
          <LazySection
            fallback={
              <SkeletonLoader
                className="h-96 bg-gray-50 dark:bg-gray-800"
                aria-label="Loading media section"
              />
            }
          >
            <Suspense
              fallback={
                <SkeletonLoader
                  className="h-96 bg-gray-50 dark:bg-gray-800"
                  aria-label="Loading media section"
                />
              }
            >
              <Media />
            </Suspense>
          </LazySection>
        </ErrorBoundary>

        <ErrorBoundary
          title="Publications Section Error"
          message="Unable to load the publications section. Please try refreshing the page."
          showRetry={true}
        >
          <LazySection
            fallback={
              <SkeletonLoader
                className="h-96 bg-white dark:bg-gray-900"
                aria-label="Loading publications section"
              />
            }
          >
            <Suspense
              fallback={
                <SkeletonLoader
                  className="h-96 bg-white dark:bg-gray-900"
                  aria-label="Loading publications section"
                />
              }
            >
              <Publications />
            </Suspense>
          </LazySection>
        </ErrorBoundary>

        <ErrorBoundary
          title="Projects Section Error"
          message="Unable to load the projects section. Please try refreshing the page."
          showRetry={true}
        >
          <LazySection
            fallback={
              <SkeletonLoader
                className="h-96 bg-white dark:bg-gray-900"
                aria-label="Loading projects section"
              />
            }
          >
            <Suspense
              fallback={
                <SkeletonLoader
                  className="h-96 bg-white dark:bg-gray-900"
                  aria-label="Loading projects section"
                />
              }
            >
              <Projects />
            </Suspense>
          </LazySection>
        </ErrorBoundary>

        <ContactSection />
      </main>
    </Layout>
  );
}

export default App;
